/**
 * Client for Edaptio's own realtime service (realtime/ in this repo), opened by
 * ./realtime.js with the `realtime` credential from /realtime/token.
 *
 * It emits on the bus in the shape every listener expects:
 *     bus.$emit(eventName, { channel, data, clientId, timestamp })
 * What matters:
 *
 * - The token lists every channel the user may hear and the service subscribes the
 *   connection to all of them (`autoSubscribe`): no per-channel attach, no
 *   per-connection channel cap, so teachers with many classes get every feed.
 * - Continuity: the client remembers the last event id and sends it on reconnect;
 *   the service replays what was missed (default window 2 minutes). Anything older
 *   arrives as `realtime:resync` on the bus — refetch.
 * - Transport: WebSocket, falling back to Server-Sent Events over fetch when a
 *   network blocks WebSockets (school filtering proxies).
 *
 * Protocol: realtime/docs/realtime.md. Pure JS on purpose: the same module runs in
 * the browser, in Vitest, and in Node for the cross-stack test (realtime/scripts).
 */

const WELCOME_TIMEOUT_MS = 6000
/** WebSocket attempts that never got a `welcome` before switching to SSE. */
const WS_FAILURES_BEFORE_SSE = 2
const REFRESH_BEFORE_EXPIRY_MS = 120 * 1000
const MAX_BACKOFF_MS = 30 * 1000
/** Event keys remembered to drop duplicates after a replay. */
const SEEN_LIMIT = 1000

/** Compares `<ms>-<seq>` ids. */
export function compareIds(a, b) {
    const [am, as] = a.split('-').map(Number)
    const [bm, bs] = b.split('-').map(Number)
    return am - bm || as - bs
}

export class NativeRealtimeClient {
    /**
     * @param {object} deps
     * @param {{ $emit: Function }} deps.bus
     * @param {{ issueToken: Function, issueUpgradedToken: Function }} deps.api
     *   Both resolve to `{ realtime: { url, token, expires_at } }` (realtimeAPI's shape).
     */
    constructor({
        bus,
        api,
        WebSocketImpl = globalThis.WebSocket,
        fetchImpl = globalThis.fetch?.bind(globalThis),
        timers = globalThis,
        random = Math.random,
        log = console,
    }) {
        this.bus = bus
        this.api = api
        this.WebSocketImpl = WebSocketImpl
        this.fetchImpl = fetchImpl
        this.timers = timers
        this.random = random
        this.log = log

        this.credentials = null
        this.transport = 'ws'
        this.connected = false
        this.stopped = true
        this.lastId = null
        this.seen = new Set()
        this.attempt = 0
        this.wsFailures = 0
        this.upgradeTarget = null
        this.ws = null
        this.sseAbort = null
        /** The service refused the current token; get a new one before the next attempt. */
        this.tokenRefused = false
        /** A refused `auth` was already retried with a fresh token on this socket. */
        this.authRetried = false
        this._timers = {}
    }

    /** Opens the connection with a `realtime` credential from /realtime/token. */
    start(credentials) {
        this.credentials = credentials
        this.stopped = false
        this._open()
    }

    stop() {
        this.stopped = true
        this.connected = false
        Object.keys(this._timers).forEach((name) => this._clear(name))
        if (this.ws) {
            try { this.ws.close(1000) } catch (e) { /* already closed */ }
            this.ws = null
        }
        if (this.sseAbort) {
            this.sseAbort.abort()
            this.sseAbort = null
        }
    }

    /**
     * Adds an on-demand channel (`board:<id>`): mint a token that also grants it and
     * present it on the open connection. Refreshes keep asking for the same upgrade.
     */
    async upgrade(type, id) {
        const { realtime } = await this.api.issueUpgradedToken(type, id)
        if (!realtime) return
        this.upgradeTarget = { type, id }
        this._useCredentials(realtime)
    }

    // --- connection ------------------------------------------------------------

    /**
     * Connects, first fetching a new token when the current one is refused, expired or
     * about to expire. A tab that slept past its token's expiry (a laptop closed for the
     * night) otherwise reconnected with the dead token: SSE got 401 on every retry, and
     * a WebSocket stayed open but unauthenticated, hearing nothing until a reload.
     */
    _open() {
        if (this.stopped) return
        if (this._tokenStale()) {
            this._refresh().then(() => this._connect(), () => this._scheduleReconnect())
            return
        }
        this._connect()
    }

    _connect() {
        if (this.stopped) return
        if (this.transport === 'sse') {
            this._openSse()
        } else {
            this._openWs()
        }
    }

    _tokenStale() {
        if (this.tokenRefused) return true
        const expiresAt = this.credentials?.expires_at
        return !!expiresAt && expiresAt * 1000 - Date.now() < REFRESH_BEFORE_EXPIRY_MS
    }

    _openWs() {
        const url = `${this.credentials.url.replace(/^http/, 'ws')}/v1/realtime`
        const ws = new this.WebSocketImpl(url)
        this.ws = ws
        this.authRetried = false
        let welcomed = false

        this._set('welcome', () => {
            if (!welcomed) {
                try { ws.close() } catch (e) { /* ignore */ }
            }
        }, WELCOME_TIMEOUT_MS)

        ws.onopen = () => {
            this._send({ type: 'auth', token: this.credentials.token, ...(this.lastId ? { since: this.lastId } : {}) })
        }
        ws.onmessage = (message) => {
            const frame = JSON.parse(message.data)
            if (frame.type === 'welcome') {
                welcomed = true
                this.wsFailures = 0
                this._clear('welcome')
            }
            this._handle(frame)
        }
        ws.onclose = (event) => {
            if (this.ws !== ws) return
            this.ws = null
            this.connected = false
            this._clear('welcome')
            if (this.stopped || event.code === 1000) return
            if (!welcomed) {
                this.wsFailures += 1
                if (this.wsFailures >= WS_FAILURES_BEFORE_SSE) {
                    this.log.warn('[realtime] WebSocket unavailable, falling back to SSE')
                    this.transport = 'sse'
                }
            }
            if (event.code === 4003) {
                this._refresh().then(() => this._open(), () => this._scheduleReconnect())
            } else {
                this._scheduleReconnect()
            }
        }
        ws.onerror = () => { /* onclose follows */ }
    }

    async _openSse() {
        const query = this.lastId ? `?since=${encodeURIComponent(this.lastId)}` : ''
        const url = `${this.credentials.url}/v1/realtime/sse${query}`
        const abort = new AbortController()
        this.sseAbort = abort
        try {
            const response = await this.fetchImpl(url, {
                headers: { Authorization: `Bearer ${this.credentials.token}`, Accept: 'text/event-stream' },
                signal: abort.signal,
            })
            if (response.status === 401) this.tokenRefused = true
            if (!response.ok) throw new Error(`SSE ${response.status}`)
            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ''
            for (;;) {
                const { done, value } = await reader.read()
                if (done) break
                buffer += decoder.decode(value, { stream: true })
                let end
                while ((end = buffer.indexOf('\n\n')) >= 0) {
                    const block = buffer.slice(0, end)
                    buffer = buffer.slice(end + 2)
                    const data = block.split('\n')
                        .filter((line) => line.startsWith('data:'))
                        .map((line) => line.slice(5).trimStart())
                        .join('\n')
                    if (data) this._handle(JSON.parse(data))
                }
            }
        } catch (err) {
            if (abort.signal.aborted) return
            this.log.warn('[realtime] SSE stream failed', err?.message)
        }
        if (this.sseAbort !== abort || this.stopped) return
        this.sseAbort = null
        this.connected = false
        // Streams end every few minutes by design; reconnect and resume.
        this._scheduleReconnect()
    }

    _scheduleReconnect() {
        if (this.stopped) return
        const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.attempt) * (0.5 + this.random() / 2)
        this.attempt += 1
        this._set('reconnect', () => this._open(), delay)
    }

    // --- frames --------------------------------------------------------------------

    _handle(frame) {
        switch (frame.type) {
        case 'welcome':
            // SSE is already authenticated by its request; WebSocket waits for `auth`.
            if (this.transport === 'sse') this._authenticated()
            break
        case 'authenticated':
            this._authenticated()
            break
        case 'event':
            this._event(frame)
            break
        case 'resync':
            this.bus.$emit('realtime:resync', { channel: frame.channel })
            break
        case 'error':
            this.log.warn('[realtime]', frame.code, frame.message, frame.channel || '')
            // Without a channel, `unauthorized` answers our `auth` (presence errors name one).
            if (frame.code === 'unauthorized' && !frame.channel) this._authRefused()
            break
        default:
            break
        }
    }

    /**
     * The service refused our `auth`, so the socket is open but hears nothing. Get a
     * new token and authenticate again; if that fails too, reconnect with backoff.
     */
    _authRefused() {
        if (!this.authRetried) {
            this.authRetried = true
            this._refresh().catch(() => this._reconnectForToken())
            return
        }
        this._reconnectForToken()
    }

    _reconnectForToken() {
        this.tokenRefused = true
        if (this.ws) {
            try { this.ws.close(4000) } catch (e) { /* already closed */ }
        }
    }

    _authenticated() {
        this.connected = true
        this.attempt = 0
        this.authRetried = false
        const expiresAt = this.credentials.expires_at
        if (expiresAt) {
            const wait = Math.max(5000, expiresAt * 1000 - Date.now() - REFRESH_BEFORE_EXPIRY_MS)
            this._set('refresh', () => {
                this._refresh().catch((err) => this.log.warn('[realtime] token refresh failed', err?.message))
            }, wait)
        }
    }

    _event(frame) {
        const key = `${frame.channel}|${frame.id}`
        if (this.seen.has(key)) return
        this.seen.add(key)
        if (this.seen.size > SEEN_LIMIT) {
            this.seen.delete(this.seen.values().next().value)
        }
        if (!this.lastId || compareIds(frame.id, this.lastId) > 0) this.lastId = frame.id

        this.bus.$emit(frame.name, {
            channel: frame.channel,
            data: frame.data,
            clientId: null,
            timestamp: Date.parse(frame.ts),
        })
    }

    // --- tokens --------------------------------------------------------------------

    async _refresh() {
        const target = this.upgradeTarget
        const { realtime } = target
            ? await this.api.issueUpgradedToken(target.type, target.id)
            : await this.api.issueToken()
        if (realtime) this._useCredentials(realtime)
    }

    /** Swap tokens: re-auth on the open WebSocket, or reopen the SSE stream. */
    _useCredentials(credentials) {
        this.credentials = credentials
        this.tokenRefused = false
        if (this.transport === 'ws' && this.ws && this.ws.readyState === 1) {
            this._send({ type: 'auth', token: credentials.token })
        } else if (this.transport === 'sse' && this.sseAbort) {
            this.sseAbort.abort()
            this.sseAbort = null
            this._open()
        }
    }

    // --- helpers -------------------------------------------------------------------

    _send(message) {
        if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify(message))
    }

    _set(name, fn, ms) {
        this._clear(name)
        this._timers[name] = this.timers.setTimeout(() => {
            delete this._timers[name]
            fn()
        }, ms)
    }

    _clear(name) {
        if (this._timers[name]) {
            this.timers.clearTimeout(this._timers[name])
            delete this._timers[name]
        }
    }
}
